'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { createContext, useContext, useMemo, type ReactNode } from 'react'

import type { Locale } from '@/i18n/routing'
import { EN } from './en'
import { formatFor, type VisualFormat } from './format'
import { NL } from './nl'
import type { VisualCopy } from './types'

/**
 * Everything the app visuals put on screen, in the language of the page they are on.
 *
 * The visuals are screenshots of Offsoo drawn in the browser, and until now they were drawn in
 * Dutch on every page — an English visitor read English copy around a Dutch application. The data
 * stays in `demo-data.ts`, which is one universe of clients, hours and percentages; what moves here
 * is only what a reader sees written: statuses, column headings, captions, dates and money.
 *
 * Client names are not translated. They are proper nouns, and a Dutch firm's client list reads as
 * a Dutch firm's client list in any language.
 */

const TABLES: Record<Locale, VisualCopy> = { nl: NL, en: EN }

type VisualCopyValue = { locale: Locale; t: VisualCopy; fmt: VisualFormat }

const VisualCopyContext = createContext<VisualCopyValue | null>(null)

export function VisualCopyProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const value = useMemo(() => {
    const t = TABLES[locale] ?? NL
    return { locale, t, fmt: formatFor(locale, t) }
  }, [locale])

  return <VisualCopyContext.Provider value={value} data-sentry-element="VisualCopyContext.Provider" data-sentry-component="VisualCopyProvider" data-sentry-source-file="index.tsx">{children}</VisualCopyContext.Provider>
}

/**
 * The copy for the language of the page. Falls back to Dutch outside a provider so a visual
 * rendered in isolation — a test, a Storybook-style page — still reads.
 */
export function useVisualCopy(): VisualCopyValue {
  const context = useContext(VisualCopyContext)
  return context ?? { locale: 'nl', t: NL, fmt: formatFor('nl', NL) }
}

export type { VisualFormat } from './format'
export {
  activityLabel,
  addressTagLabel,
  dossierTaskLabel,
  excelCellLabel,
  fileLabel,
  leadTimeStatus,
  legalFormLabel,
  monthLabel,
} from './resolve'
export type { VisualCopy } from './types'
